import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";

const FLOW = ["awaiting_transfer", "received", "fulfilled", "shipped"];
const LABEL = {
  awaiting_transfer: "Awaiting Transfer",
  received: "Payment Received",
  fulfilled: "Fulfilled (Stone Reserved)",
  shipped: "Shipped",
  cancelled: "Cancelled",
};
const COLOR = {
  awaiting_transfer: "text-yellow-400 border-yellow-400/40",
  received: "text-blue-300 border-blue-300/40",
  fulfilled: "text-gold border-gold/40",
  shipped: "text-emerald-400 border-emerald-400/40",
  cancelled: "text-red-400 border-red-400/40",
};

const OrderTracking = () => {
  const [stripe, setStripe] = useState([]);
  const [bank, setBank] = useState([]);
  const [view, setView] = useState("bank");
  const [tracking, setTracking] = useState({});

  const load = useCallback(async () => {
    const [s, b] = await Promise.all([
      api.get("/admin/transactions").catch(() => ({ data: [] })),
      api.get("/admin/bank-orders").catch(() => ({ data: [] })),
    ]);
    setStripe(s.data); setBank(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const advance = async (id, ref, currentStatus) => {
    const idx = FLOW.indexOf(currentStatus);
    if (idx === -1 || idx === FLOW.length - 1) return;
    const next = FLOW[idx + 1];
    const trackingNumber = next === "shipped" ? tracking[id] : undefined;
    if (next === "shipped" && !trackingNumber) {
      toast.error("Enter a tracking number before marking shipped.");
      return;
    }
    try {
      await api.patch(`/admin/bank-orders/${id}`, { status: next, tracking_number: trackingNumber });
      toast.success(`${ref} → ${LABEL[next]}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const cancel = async (id, ref) => {
    if (!window.confirm(`Cancel order ${ref}?`)) return;
    try {
      await api.patch(`/admin/bank-orders/${id}`, { status: "cancelled" });
      toast.success("Order cancelled.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="order-tracking" className="pb-32">
      <div className="flex gap-3 border-b border-white/10 pb-6 mb-8">
        <button data-testid="ord-tab-bank" onClick={() => setView("bank")}
          className={`font-accent text-[11px] tracking-[0.28em] uppercase px-5 py-3 transition-colors ${view === "bank" ? "text-gold border-b border-gold -mb-[25px] pb-6" : "text-white/50 hover:text-white"}`}>
          Bank Wire Orders <span className="ml-2 text-white/30">({bank.length})</span>
        </button>
        <button data-testid="ord-tab-stripe" onClick={() => setView("stripe")}
          className={`font-accent text-[11px] tracking-[0.28em] uppercase px-5 py-3 transition-colors ${view === "stripe" ? "text-gold border-b border-gold -mb-[25px] pb-6" : "text-white/50 hover:text-white"}`}>
          Stripe Transactions <span className="ml-2 text-white/30">({stripe.length})</span>
        </button>
      </div>

      {view === "bank" && (
        <div className="space-y-4">
          {bank.map((b) => (
            <div key={b.id} data-testid={`bank-row-${b.id}`} className="bg-onyx border border-white/10 p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                  <span className={`font-accent text-[10px] tracking-[0.24em] uppercase px-3 py-1 border ${COLOR[b.status]}`}>
                    {LABEL[b.status] || b.status}
                  </span>
                  <span className="ml-3 font-accent text-gold tracking-[0.18em] text-xs">{b.reference}</span>
                </div>
                <span className="text-white/40 text-xs">{new Date(b.created_at).toLocaleString()}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm mb-4">
                <div><div className="text-white/40 text-xs uppercase tracking-[0.18em] font-accent mb-1">Stone</div><div className="font-heading text-lg">{b.gemstone_name}</div></div>
                <div><div className="text-white/40 text-xs uppercase tracking-[0.18em] font-accent mb-1">Amount</div><div className="text-ivory font-heading">{Intl.NumberFormat("en-US").format(b.amount)} {b.currency}</div></div>
                <div><div className="text-white/40 text-xs uppercase tracking-[0.18em] font-accent mb-1">Buyer</div><div className="text-ivory">{b.name}</div><div className="text-white/50 text-xs">{b.email}</div></div>
                <div><div className="text-white/40 text-xs uppercase tracking-[0.18em] font-accent mb-1">Phone</div><div>{b.phone || "—"}</div></div>
              </div>
              {b.notes && <div className="text-white/60 text-sm mb-4 italic">Note: {b.notes}</div>}
              {b.tracking_number && <div className="text-emerald-400 text-sm mb-4">Tracking: {b.tracking_number}</div>}

              {b.status !== "shipped" && b.status !== "cancelled" && (
                <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-white/10">
                  {FLOW.indexOf(b.status) === FLOW.length - 2 && (
                    <input data-testid={`bank-track-${b.id}`} placeholder="Tracking # (e.g. DHL 1234567890)" value={tracking[b.id] || ""} onChange={(e) => setTracking({...tracking, [b.id]: e.target.value})}
                      className="bg-transparent border border-white/20 px-3 py-2 text-white focus:outline-none focus:border-gold text-sm w-72" />
                  )}
                  <button data-testid={`bank-advance-${b.id}`} onClick={() => advance(b.id, b.reference, b.status)}
                    className="bg-gold text-black font-accent uppercase tracking-[0.24em] text-[11px] px-5 py-2 hover:bg-ivory">
                    Mark {LABEL[FLOW[FLOW.indexOf(b.status) + 1]] || ""}
                  </button>
                  <button data-testid={`bank-cancel-${b.id}`} onClick={() => cancel(b.id, b.reference)}
                    className="border border-red-500/40 text-red-400 font-accent uppercase tracking-[0.24em] text-[11px] px-5 py-2 hover:bg-red-500/10">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))}
          {bank.length === 0 && <div className="text-white/40 text-center py-12">No bank orders yet.</div>}
        </div>
      )}

      {view === "stripe" && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-white/40 font-accent text-[10px] tracking-[0.28em] uppercase border-b border-white/10">
              <tr><th className="text-left py-4">Stone</th><th className="text-left">Amount</th><th className="text-left">Status</th><th className="text-left">Buyer</th><th className="text-left">Session</th><th className="text-left">Date</th></tr>
            </thead>
            <tbody>
              {stripe.map((t) => (
                <tr key={t.session_id} className="border-b border-white/5">
                  <td className="py-4 font-heading text-lg">{t.gemstone_name}</td>
                  <td className="text-ivory">{Intl.NumberFormat("en-US").format(t.amount)} {t.currency}</td>
                  <td className={t.payment_status === "paid" ? "text-emerald-400" : "text-yellow-400"}>{t.payment_status}</td>
                  <td>{t.user_email || <span className="text-white/40">guest</span>}</td>
                  <td className="text-white/40 font-accent tracking-[0.15em] text-xs">{t.session_id?.slice(0, 14)}…</td>
                  <td className="text-white/50">{new Date(t.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {stripe.length === 0 && <div className="text-white/40 text-center py-12">No Stripe transactions yet.</div>}
        </div>
      )}
    </div>
  );
};

export default OrderTracking;
